/* eslint-disable @typescript-eslint/restrict-template-expressions */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button, Col, Dropdown, Form, Input, Menu, Row, Select, Table } from 'antd';
import { DownOutlined, PlusOutlined } from '@ant-design/icons';
import { useModal } from '@ebay/nice-modal-react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '@isocel/front-common';
import moment from 'moment';

import { PackagingFlowDirection, PackagingFlowItem, RoleSlug, StoreUser } from '../../queries/api/types';
import { useStoreByOrganization } from '../../queries/store';
import { getRoute, RoutePathName } from '../../routes';
import {
    getAllPackagingFlow,
    getPackingFlowFromStore,
    getPackingFlowFromVoucher,
    validateTransportVoucher,
} from '../../queries/api/packaging-flow';
import { fetchPackages } from '../../queries/api/package';
import { usePackagingFlow } from '../../context/PackagingFlowContext';
import { usePalettes } from '../../queries/palette';
import TableDetailsFormDrawer from '../dashboard/details/TableDetailsDrawer';
import { useNotification } from '../notification/Notification';
import './ReceptionsAValider.less';

interface DataType {
    key: string;
    sender: string;
    senderId: string;
    reciever: string;
    date: string;
    unit: string;
    transportVoucher: string;
    flowType: PackagingFlowDirection | null;
    received: boolean;
    packages: any[];
    children?: DataType[];
    [key: string]: any;
}

interface Props {
    onCountChange?: (count: number) => void;
}

const ReceptionsAValider: React.FC<Props> = ({ onCountChange }) => {
    const [data, setData] = useState<DataType[]>([]);
    const packagesRef = useRef<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({ page: 1, pageSize: 10 });
    const [total, setTotal] = useState(0);
    const [filterVoucher, setFilterVoucher] = useState('');
    const [filterStoreIds, setFilterStoreIds] = useState<string[]>([]);
    const [pendingVoucher, setPendingVoucher] = useState('');
    const [pendingStoreIds, setPendingStoreIds] = useState<string[]>([]);

    const { user } = useAuth();
    const storeId = (user as StoreUser)?.store || '';
    const isStoreUser = user?.role?.slug === RoleSlug.storeAdmin || user?.role?.slug === RoleSlug.storeUser;
    const isOrgUser = user?.role?.slug === RoleSlug.orgAdmin || user?.role?.slug === RoleSlug.orgUser;
    const history = useHistory();
    const {
        reset,
        setSelectedStore,
        setPackageFlow,
        setFlowType,
        setVoucher,
        setWarehouse,
        setSelectedParc,
        setParcPalettes,
    } = usePackagingFlow();
    const { showNotification } = useNotification();
    const tableDetailsFormDrawer = useModal(TableDetailsFormDrawer);
    const { data: parcPalettesData } = usePalettes(user?.organization?.id || '');
    const { data: storesData } = useStoreByOrganization(isOrgUser ? user?.organization?.id ?? '' : '');

    const fetchPackagesData = async () => {
        const response = await fetchPackages();
        packagesRef.current = response;
    };

    const getPackageModelName = useCallback((pkg: any) => {
        if (typeof pkg === 'string') return packagesRef.current.find((p) => p.id === pkg)?.model.name || '';
        return pkg.model.name;
    }, []);

    const getPackageTypeName = useCallback((pkg: any) => {
        if (typeof pkg === 'string') return packagesRef.current.find((p) => p.id === pkg)?.type.name || '';
        return pkg.type.name;
    }, []);

    const fetchData = useCallback(async () => {
        const canFetch = isStoreUser ? !!storeId : isOrgUser;
        if (!canFetch) return;
        setLoading(true);
        try {
            const params = isStoreUser
                ? {
                      page: pagination.page,
                      pageSize: pagination.pageSize,
                      flowType: PackagingFlowDirection.ShippedToStore,
                      storeIds: [storeId],
                      received: false,
                      sortField: 'createdAt',
                      sortOrder: 'desc',
                      ...(filterVoucher ? { transportVoucher: filterVoucher } : {}),
                  }
                : {
                      page: pagination.page,
                      pageSize: pagination.pageSize,
                      flowType: PackagingFlowDirection.ShippedToOrganization,
                      received: false,
                      sortField: 'createdAt',
                      sortOrder: 'desc',
                      ...(filterVoucher ? { transportVoucher: filterVoucher } : {}),
                      ...(filterStoreIds.length > 0 ? { storeIds: filterStoreIds } : {}),
                  };

            const response = await getAllPackagingFlow(params, { organization: { id: user?.organization?.id ?? '' } });

            const rows: DataType[] = response.items.map((flow) => {
                const allPackages = flow.packages.map((pkgItem: PackagingFlowItem) => ({
                    ...pkgItem,
                    model: getPackageModelName(pkgItem.package),
                    type: getPackageTypeName(pkgItem.package),
                }));

                const unitCount = allPackages.reduce((acc: number, p: any) => {
                    const count = Number(p.goodCount) + Number(p.badCount);
                    return acc + (count === 0 ? Number(p.shippedQuantity) : count);
                }, 0);

                return {
                    key: flow._id || '',
                    sender: isStoreUser
                        ? `Entrepôt - ${flow.sender?.name || ''}`
                        : `Magasin - ${flow.sender?.name || ''}`,
                    senderId: flow.sender?._id || '',
                    reciever: `Magasin - ${flow.recipient?.name || ''}`,
                    recieverId: flow.recipient?._id || '',
                    date: flow.expeditionAt
                        ? moment(flow.expeditionAt).format('DD/MM/YYYY HH:mm')
                        : moment(flow.createdAt).format('DD/MM/YYYY HH:mm'),
                    unit: unitCount.toString(),
                    transportVoucher: flow.transportVoucher || '',
                    flowType: flow.flowType,
                    received: flow.received || false,
                    packages: allPackages,
                    details: '',
                    comment: flow.comment || '',
                    loadingSession: flow.loadingSession,
                    transporter: flow.transporter || null,
                    originId: flow.originId || null,
                    cmr: flow.cmr || '',
                    packagingVouchers: flow.packagingVouchers || [],
                    packagingVoucherUsed: flow.packagingVoucherUsed || [],
                    userInfo: flow.user,
                    attachments: flow.attachments || [],
                    differenceReported: flow.differenceReported || false,
                    expeditionAt: flow.expeditionAt
                        ? moment(flow.expeditionAt).format('DD/MM/YYYY HH:mm')
                        : moment(flow.createdAt).format('DD/MM/YYYY HH:mm'),
                    deliveredAt: flow.deliveredAt ? moment(flow.deliveredAt).format('DD/MM/YYYY HH:mm') : '',
                    truckNumber: flow.truckNumber || '',
                    orderNumber: flow.orderNumber || '',
                    children: allPackages.map((pkgItem: any) => ({
                        key: pkgItem._id || Math.random().toString(),
                        sender: '',
                        senderId: '',
                        reciever: '',
                        date: '',
                        unit: '',
                        transportVoucher: '',
                        flowType: null,
                        received: false,
                        packages: [pkgItem],
                        loadingSession: [],
                        packagingVouchers: [],
                        packagingVoucherUsed: [],
                        transporter: '',
                        cmr: '',
                        originId: '',
                        differenceReported: false,
                        attachments: [],
                    })),
                };
            });

            setData(rows);
            setTotal(response.totalCount);
            onCountChange?.(response.totalCount);
        } catch (error) {
            console.error('Error fetching receptions:', error);
        } finally {
            setLoading(false);
        }
    }, [storeId, isStoreUser, isOrgUser, pagination, onCountChange, user, filterVoucher, filterStoreIds]);

    useEffect(() => {
        fetchPackagesData();
    }, []);

    useEffect(() => {
        if (isStoreUser && storeId) {
            fetchData();
        } else if (isOrgUser) {
            fetchData();
        }
    }, [storeId, isStoreUser, isOrgUser, fetchData]);

    const handleDetailsClick = (record: DataType) => {
        tableDetailsFormDrawer.show({ data: record, storeId, refreshDashboard: fetchData });
    };

    const handleReceptionClick = useCallback(
        async (record: DataType) => {
            try {
                const res = await getPackingFlowFromVoucher(record.transportVoucher);
                if (res) {
                    reset();
                    res.flowType = PackagingFlowDirection.RecivedInStore;
                    setFlowType(PackagingFlowDirection.RecivedInStore);
                    setSelectedStore(res.recipient);
                    setVoucher(res.transportVoucher || '');
                    setWarehouse(res.sender);
                    const updatedResponse = {
                        ...res,
                        packages: res.packages.map((pkg: PackagingFlowItem) => ({
                            ...pkg,
                            goodCount: pkg.goodCount === 0 && pkg.badCount === 0 ? pkg.shippedQuantity : pkg.goodCount,
                        })),
                    };
                    setPackageFlow(updatedResponse);
                    history.push(`/recipientMovement/${storeId}`);
                }
            } catch (error) {
                console.error('Error loading reception flow:', error);
            }
        },
        [reset, setSelectedStore, setPackageFlow, setFlowType, setVoucher, setWarehouse, history, storeId]
    );

    const handleOrgReceptionClick = useCallback(
        async (record: DataType, parcName: string) => {
            const parc = (parcPalettesData || []).find((p) => p.name === parcName);
            if (!parc) return;
            try {
                const validateVoucher = await validateTransportVoucher(record.transportVoucher, 3, record.senderId);
                if (validateVoucher.voucherFound === true) {
                    showNotification(
                        'error',
                        'Erreur',
                        `Le bon de transport ${record.transportVoucher} a déjà été réceptionné par le parc palette.`
                    );
                    fetchData();
                    return;
                }
                reset();
                setFlowType(PackagingFlowDirection.RecivedInOrganization);
                setSelectedStore({
                    name: record.sender,
                    id: record.senderId,
                    _id: record.senderId,
                    code: '',
                    organization: '',
                });
                setVoucher(record.transportVoucher);
                setSelectedParc(parc);
                setParcPalettes(parcPalettesData || []);
                try {
                    const flow = await getPackingFlowFromStore(record.senderId, record.transportVoucher);
                    if (flow) {
                        setPackageFlow(flow);
                        history.push(`/recipientMovement/${record.senderId}`);
                    } else {
                        setPackageFlow(null);
                        history.push(`/recipientTrack/${record.senderId}`);
                    }
                } catch {
                    setPackageFlow(null);
                    history.push(`/recipientTrack/${record.senderId}`);
                }
            } catch (error) {
                console.error('Error validating voucher:', error);
            }
        },
        [
            parcPalettesData,
            reset,
            setFlowType,
            setSelectedStore,
            setVoucher,
            setSelectedParc,
            setParcPalettes,
            setPackageFlow,
            history,
            showNotification,
            fetchData,
        ]
    );

    if (!isStoreUser && !isOrgUser) return null;

    const columns = [
        {
            title: 'Émetteur',
            dataIndex: 'sender',
            key: 'sender',
        },
        {
            title: "Date d'expédition",
            dataIndex: 'date',
            key: 'date',
        },
        {
            title: 'Unité',
            dataIndex: 'unit',
            key: 'unit',
            render: (_: string, record: DataType) => (
                <>
                    {record.children && <div>{record.unit}</div>}
                    {!record.children &&
                        record.packages?.map((pkg: any, index: number) => (
                            <div key={index}>
                                {Number(pkg.goodCount) + Number(pkg.badCount)} -{' '}
                                {pkg.type === pkg.model ? pkg.type : `${pkg.type} ${pkg.model}`}
                            </div>
                        ))}
                </>
            ),
        },
        {
            title: 'Bon de transport',
            dataIndex: 'transportVoucher',
            key: 'transportVoucher',
            render: (_: string, record: DataType) => {
                if (!record.children) return null;
                return <div>{record.transportVoucher}</div>;
            },
        },
        {
            title: 'Action',
            key: 'action',
            render: (_: any, record: DataType) => {
                if (!record.children) return null;
                return (
                    <div style={{ display: 'flex', gap: 8 }}>
                        <Button size="small" type="primary" onClick={() => handleDetailsClick(record)}>
                            Détails
                        </Button>
                        {isStoreUser && (
                            <Button
                                size="small"
                                type="default"
                                onClick={async () => await handleReceptionClick(record)}
                            >
                                Réceptionner
                            </Button>
                        )}
                        {isOrgUser && (
                            <Dropdown
                                overlay={
                                    <Menu onClick={async ({ key }) => await handleOrgReceptionClick(record, key)}>
                                        {(parcPalettesData || []).map((parc) => (
                                            <Menu.Item key={parc.name}>{parc.name}</Menu.Item>
                                        ))}
                                    </Menu>
                                }
                                trigger={['click']}
                            >
                                <Button size="small" type="default">
                                    Réceptionner
                                </Button>
                            </Dropdown>
                        )}
                    </div>
                );
            },
        },
    ];

    const applyFilters = () => {
        setFilterVoucher(pendingVoucher);
        setFilterStoreIds(pendingStoreIds);
        setPagination((p) => ({ ...p, page: 1 }));
    };

    return (
        <div className="receptions-a-valider-container">
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 }}>
                <h1 className="receptions-a-valider-title" style={{ marginBottom: 0 }}>
                    Réceptions à valider{total > 0 && <span className="count-badge">{total}</span>}
                </h1>
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() =>
                        history.push(
                            isOrgUser ? getRoute(RoutePathName.receptionInfo) : getRoute(RoutePathName.receptionStore)
                        )
                    }
                >
                    Créer une réception
                </Button>
            </div>
            <Form layout="vertical">
                <Row gutter={16} align="bottom">
                    <Col xs={24} sm={24} md={12} lg={8}>
                        <Form.Item label="Bon de transport" className="bold-label">
                            <Input
                                placeholder="Bon de transport"
                                value={pendingVoucher}
                                onChange={(e) => setPendingVoucher(e.target.value)}
                                allowClear
                            />
                        </Form.Item>
                    </Col>
                    {isOrgUser && (
                        <Col xs={24} sm={24} md={12} lg={8}>
                            <Form.Item label="Magasin" className="bold-label">
                                <Select
                                    mode="multiple"
                                    placeholder="Magasin"
                                    value={pendingStoreIds}
                                    onChange={(values) => setPendingStoreIds(values)}
                                    allowClear
                                    showSearch
                                    optionFilterProp="label"
                                    style={{ width: '100%' }}
                                    options={(storesData || []).map((s) => ({
                                        label: `${s.code} - ${s.name}`,
                                        value: s.id,
                                    }))}
                                />
                            </Form.Item>
                        </Col>
                    )}
                    <Col xs={24} sm={24} md={12} lg={4}>
                        <Form.Item>
                            <Button type="primary" onClick={applyFilters}>
                                Appliquer les filtres
                            </Button>
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
            <Table
                size="small"
                columns={columns}
                dataSource={data}
                loading={loading}
                pagination={{
                    current: pagination.page,
                    pageSize: pagination.pageSize,
                    total,
                    showSizeChanger: true,
                    position: ['bottomRight'],
                    onChange: (page, pageSize) => setPagination({ page, pageSize }),
                }}
                expandable={{
                    expandIcon: ({ expanded, onExpand, record }) => {
                        if (!record.children) return <></>;
                        return (
                            <DownOutlined
                                className="expand-icon"
                                onClick={(e) => onExpand(record, e)}
                                style={{ transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
                            />
                        );
                    },
                }}
            />
        </div>
    );
};

export default ReceptionsAValider;
